import React, { useEffect, useMemo, useRef, useState } from 'react';

import { downloadLegacyDashboardExport, loadLegacyDashboard } from './api';
import SearchableSelect from './SearchableSelect';


const emptyDashboard = {
    filters: {
        start_date: '',
        end_date: '',
        location: '',
        location_choices: [],
    },
    module_cards: [],
    chart: {
        items: [],
    },
    daily_report: [],
};

const AUTO_REFRESH_INTERVAL_MS = 30000;


function toLocalIsoDate(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}


function getCurrentDefaultDateRange() {
    const today = new Date();
    const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
    return {
        start_date: toLocalIsoDate(monthStart),
        end_date: toLocalIsoDate(today),
    };
}


function isCurrentDefaultDateRange(candidate) {
    const defaultRange = getCurrentDefaultDateRange();
    return (
        candidate?.start_date === defaultRange.start_date
        && candidate?.end_date === defaultRange.end_date
    );
}


function sameAppliedFilterValues(first, second) {
    return (
        (first?.start_date || '') === (second?.start_date || '')
        && (first?.end_date || '') === (second?.end_date || '')
        && (first?.location || '') === (second?.location || '')
    );
}


function buildDashboardRequestParams(filters, { cacheBust = false, liveDefaultRange = false } = {}) {
    const params = {};

    if (!liveDefaultRange) {
        if (filters?.start_date) {
            params.start_date = filters.start_date;
        }
        if (filters?.end_date) {
            params.end_date = filters.end_date;
        }
    }

    if (typeof filters?.location === 'string') {
        params.location = filters.location;
    }

    if (cacheBust) {
        params._refresh = Date.now();
    }

    return params;
}


function splitSystemTitle(title) {
    if (!title.startsWith('SYSTEM ')) {
        return { prefix: '', value: title };
    }
    return {
        prefix: 'SYSTEM',
        value: title.replace(/^SYSTEM\s+/, ''),
    };
}


export default function DashboardPage({ initialDashboard }) {
    const initialFilters = initialDashboard?.filters || emptyDashboard.filters;
    const [dashboard, setDashboard] = useState(initialDashboard || emptyDashboard);
    const [filters, setFilters] = useState(initialFilters);
    const [activeFilters, setActiveFilters] = useState(initialFilters);
    const [loading, setLoading] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState('');
    const [lastUpdatedAt, setLastUpdatedAt] = useState(() => new Date());
    const requestInFlightRef = useRef(false);
    const filtersRef = useRef(initialFilters);
    const activeFiltersRef = useRef(initialFilters);
    const liveDefaultRangeRef = useRef(isCurrentDefaultDateRange(initialFilters));

    useEffect(() => {
        const nextInitialFilters = initialDashboard?.filters || emptyDashboard.filters;
        setDashboard(initialDashboard || emptyDashboard);
        setFilters(nextInitialFilters);
        setActiveFilters(nextInitialFilters);
        liveDefaultRangeRef.current = isCurrentDefaultDateRange(nextInitialFilters);
        setError('');
        setLastUpdatedAt(new Date());
    }, [initialDashboard]);

    useEffect(() => {
        filtersRef.current = filters;
    }, [filters]);

    useEffect(() => {
        activeFiltersRef.current = activeFilters;
    }, [activeFilters]);

    const chartMax = useMemo(() => {
        const values = (dashboard.chart?.items || []).flatMap((item) => [item.done || 0, item.plan || 0]);
        return Math.max(1, ...values);
    }, [dashboard.chart]);

    function handleFilterChange(event) {
        const { name, value } = event.target;
        setFilters((current) => ({
            ...current,
            [name]: value,
        }));
    }

    async function refreshDashboard(nextFilters, { silent = false } = {}) {
        if (requestInFlightRef.current) {
            return;
        }

        requestInFlightRef.current = true;
        if (silent) {
            setRefreshing(true);
        } else {
            setLoading(true);
            setError('');
        }

        try {
            const liveDefaultRange = silent && liveDefaultRangeRef.current;
            const shouldSyncDraftFilters = (
                silent
                && liveDefaultRange
                && sameAppliedFilterValues(filtersRef.current, activeFiltersRef.current)
            );
            const requestParams = buildDashboardRequestParams(nextFilters, {
                cacheBust: silent,
                liveDefaultRange,
            });
            const nextDashboard = await loadLegacyDashboard(requestParams);
            setDashboard(nextDashboard);
            setActiveFilters(nextDashboard.filters);
            liveDefaultRangeRef.current = isCurrentDefaultDateRange(nextDashboard.filters);
            if (!silent || shouldSyncDraftFilters) {
                setFilters(nextDashboard.filters);
            }
            setLastUpdatedAt(new Date());
        } catch (requestError) {
            if (!silent) {
                setError(requestError?.response?.data?.detail || 'Не удалось обновить главную страницу.');
            }
        } finally {
            requestInFlightRef.current = false;
            if (silent) {
                setRefreshing(false);
            } else {
                setLoading(false);
            }
        }
    }

    async function handleApply(event) {
        event.preventDefault();
        await refreshDashboard(filters);
    }

    async function handleExport() {
        setLoading(true);
        setError('');

        try {
            await downloadLegacyDashboardExport(filters);
        } catch (requestError) {
            setError(requestError?.response?.data?.detail || 'Не удалось выгрузить отчёт.');
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        const runAutoRefresh = () => {
            if (document.visibilityState !== 'visible') {
                return;
            }
            refreshDashboard(activeFiltersRef.current, { silent: true });
        };

        const intervalId = window.setInterval(runAutoRefresh, AUTO_REFRESH_INTERVAL_MS);
        document.addEventListener('visibilitychange', runAutoRefresh);

        return () => {
            window.clearInterval(intervalId);
            document.removeEventListener('visibilitychange', runAutoRefresh);
        };
    }, []);

    const formattedLastUpdatedAt = useMemo(() => (
        lastUpdatedAt.toLocaleTimeString('ru-RU', {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
        })
    ), [lastUpdatedAt]);

    return (
        <div className="shell-page shell-page--dashboardLegacy">
            <section className="legacy-dashboardSection">
                <div className="legacy-dashboardSectionHeading">
                    <h3>Информация о модулях</h3>
                    <div className="legacy-dashboardLiveMeta">
                        <span>Автообновление каждые 30 секунд</span>
                        <strong>
                            {refreshing ? 'Обновляем…' : `Обновлено: ${formattedLastUpdatedAt}`}
                        </strong>
                    </div>
                </div>

                <div className="legacy-dashboardModuleGrid">
                    {dashboard.module_cards.map((card) => {
                        const title = splitSystemTitle(card.title);
                        return (
                            <article key={card.system} className="legacy-dashboardModuleCard">
                                <div className="legacy-dashboardModuleTitle">
                                    {title.prefix ? <span>{title.prefix}</span> : null}
                                    <strong>{title.value}</strong>
                                </div>
                                <dl className="legacy-dashboardModuleMeta">
                                    <div>
                                        <dt>BAD</dt>
                                        <dd>{card.total_bad}</dd>
                                    </div>
                                    <div>
                                        <dt>GOOD</dt>
                                        <dd>{card.total_good}</dd>
                                    </div>
                                    <div>
                                        <dt>На сборке</dt>
                                        <dd>{card.total_assembling}</dd>
                                    </div>
                                    <div>
                                        <dt>Готово к отгрузке</dt>
                                        <dd>{card.total_ready_to_ship}</dd>
                                    </div>
                                </dl>
                            </article>
                        );
                    })}
                </div>
            </section>

            <section className="legacy-dashboardSection">
                <form className="legacy-dashboardToolbar" onSubmit={handleApply}>
                    <button
                        type="button"
                        className="legacy-dashboardButton legacy-dashboardButton--secondary"
                        onClick={handleExport}
                        disabled={loading}
                    >
                        Экспорт
                    </button>

                    <label className="legacy-dashboardField">
                        <span>Период от</span>
                        <input
                            type="date"
                            name="start_date"
                            value={filters.start_date || ''}
                            onChange={handleFilterChange}
                            disabled={loading}
                        />
                    </label>

                    <label className="legacy-dashboardField">
                        <span>Период до</span>
                        <input
                            type="date"
                            name="end_date"
                            value={filters.end_date || ''}
                            onChange={handleFilterChange}
                            disabled={loading}
                        />
                    </label>

                    <label className="legacy-dashboardField legacy-dashboardField--location">
                        <span>Локация</span>
                        <SearchableSelect
                            value={filters.location || ''}
                            options={filters.location_choices || []}
                            onChange={(value) => setFilters((current) => ({
                                ...current,
                                location: value,
                            }))}
                            placeholder="Все локации"
                            disabled={loading}
                        />
                    </label>

                    <button type="submit" className="legacy-dashboardButton" disabled={loading}>
                        {loading ? 'Обновляем...' : 'Показать'}
                    </button>
                </form>

                {error ? <div className="legacy-dashboardError">{error}</div> : null}

                <div className="legacy-dashboardChartCard">
                    <div className="legacy-dashboardChartLegend">
                        <span>
                            <i className="legacy-dashboardLegendSwatch legacy-dashboardLegendSwatch--done" />
                            Done
                        </span>
                        <span>
                            <i className="legacy-dashboardLegendSwatch legacy-dashboardLegendSwatch--plan" />
                            Current plan
                        </span>
                    </div>

                    <div className="legacy-dashboardChart">
                        {dashboard.chart.items.map((item) => {
                            const planHeight = `${((item.plan || 0) / chartMax) * 100}%`;
                            const doneHeight = `${((item.done || 0) / chartMax) * 100}%`;
                            return (
                                <div key={item.system} className="legacy-dashboardChartColumn">
                                    <div className="legacy-dashboardChartValueRow">
                                        <span>{item.done}</span>
                                        <span>{item.plan}</span>
                                    </div>
                                    <div className="legacy-dashboardChartBars">
                                        <div className="legacy-dashboardChartPlan" style={{ height: planHeight }} />
                                        <div className="legacy-dashboardChartDone" style={{ height: doneHeight }} />
                                    </div>
                                    <div className="legacy-dashboardChartLabel">{item.system}</div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            <section className="legacy-dashboardSection">
                <div className="legacy-dashboardSectionHeading">
                    <h3>Ежедневный отчёт</h3>
                </div>

                <div className="legacy-dashboardReportList">
                    {dashboard.daily_report.length ? (
                        dashboard.daily_report.map((group) => (
                            <section key={group.date} className="legacy-dashboardReportGroup">
                                <div className="legacy-dashboardReportDate">{group.date}</div>
                                <div className="legacy-dashboardTableWrap">
                                    <table className="legacy-dashboardTable">
                                        <thead>
                                            <tr>
                                                <th>Mecha</th>
                                                <th>Сколько привезли</th>
                                                <th>Сколько отремонтировано</th>
                                                <th>Отказ</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {group.rows.map((row) => (
                                                <tr key={`${group.date}-${row.party_number}`}>
                                                    <td>{row.party_number}</td>
                                                    <td>{row.arrival_count}</td>
                                                    <td>{row.repaired_count}</td>
                                                    <td>{row.failure_count}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </section>
                        ))
                    ) : (
                        <div className="shell-emptyState">За выбранный период данных нет.</div>
                    )}
                </div>
            </section>
        </div>
    );
}
